import { useCallback, useEffect, useRef, useState } from 'react';

import {
  LoyaltyOstType,
  LoyaltyServiceOrderSelectionStatus,
  LoyaltyServiceOstType,
  OrderSelectionStatus,
  useLoyaltyServiceOrderSelectionQuery,
  useOrderSelectionByMultipleTypeQuery,
} from 'generated/graphql-gateway';

import {
  IUseShortCodePollStatus,
  IUseShortCodePollStatusParams,
  ShortCodePollingStatus,
} from './types';

export const SHORT_CODE_NOT_FOUND_ERROR_CODE = 'HashSetNotFoundError';

export const useShortCodePollStatus = ({
  loyaltyId,
  skipPolling,
  shortCode,
  pollInterval,
  backend,
}: IUseShortCodePollStatusParams): IUseShortCodePollStatus => {
  // Poll through the same backend the short code was actually created on
  // (passed in from useShortCode's persisted `backend`), not a live
  // recomputation of the loyalty-service eligibility flags/experiment.
  // Those can change value between when the code was created and a later
  // render (e.g. the render-ready experiment resolves asynchronously and
  // starts `false`) — recomputing here would then poll a different backend
  // than the one the code actually lives on, and the scan would never be
  // observed. See BKPE-27933.
  const enableLoyaltyServiceOrderSelection = backend === 'loyalty-service';
  const [pollingStatus, setPollingStatus] = useState<ShortCodePollingStatus>(
    ShortCodePollingStatus.None
  );

  const resetPollingStatus = useCallback(() => {
    setPollingStatus(ShortCodePollingStatus.None);
  }, []);

  // setting pending code state
  useEffect(() => {
    if (!skipPolling) {
      setPollingStatus(ShortCodePollingStatus.Pending);
    }
  }, [skipPolling]);

  const skip =
    skipPolling || !shortCode || !loyaltyId || pollingStatus === ShortCodePollingStatus.None;
  // short code polling
  const legacyOrderSelectionQuery = useOrderSelectionByMultipleTypeQuery({
    variables: {
      input: [
        {
          key: shortCode!,
          ostType: LoyaltyOstType.OTP,
        },
        {
          key: loyaltyId!,
          ostType: LoyaltyOstType.LOYALTY_ID,
        },
      ],
    },
    fetchPolicy: 'no-cache',
    skip: skip || enableLoyaltyServiceOrderSelection,
    notifyOnNetworkStatusChange: true,
    onCompleted(data) {
      data?.loyaltyOrderSelectionByMultipleType?.forEach(sel => {
        if (sel?.status === OrderSelectionStatus.CLAIMED) {
          setPollingStatus(ShortCodePollingStatus.Confirmed);
        }
      });
    },
  });
  const loyaltyServiceOrderSelectionQuery = useLoyaltyServiceOrderSelectionQuery({
    variables: {
      input: [
        {
          key: shortCode!,
          type: LoyaltyServiceOstType.OTP,
        },
        {
          key: loyaltyId!,
          type: LoyaltyServiceOstType.LOYALTY_ID,
        },
      ],
    },
    fetchPolicy: 'no-cache',
    skip: skip || !enableLoyaltyServiceOrderSelection,
    notifyOnNetworkStatusChange: true,
    onCompleted(data) {
      data?.loyaltyServiceOrderSelection?.forEach(sel => {
        if (sel?.status === LoyaltyServiceOrderSelectionStatus.CLAIMED) {
          setPollingStatus(ShortCodePollingStatus.Confirmed);
        }
      });
    },
  });
  const { error, startPolling, stopPolling } = enableLoyaltyServiceOrderSelection
    ? loyaltyServiceOrderSelectionQuery
    : legacyOrderSelectionQuery;

  const startPollingRef = useRef(startPolling);
  const stopPollingRef = useRef(stopPolling);
  startPollingRef.current = startPolling;
  stopPollingRef.current = stopPolling;

  useEffect(() => {
    const stopSelectedPolling = stopPollingRef.current;

    // skip is going to be true, if the polling status is 'pending' after 5 minutes
    // for example when the user is in a different screen
    if (skip || pollingStatus === ShortCodePollingStatus.Confirmed) {
      stopSelectedPolling();
    } else {
      startPollingRef.current(pollInterval);
    }

    return () => {
      stopSelectedPolling();
    };
  }, [backend, pollInterval, skip, pollingStatus]);

  useEffect(() => {
    const isNotFoundError = error?.graphQLErrors?.some(
      err => err?.extensions?.code === SHORT_CODE_NOT_FOUND_ERROR_CODE
    );
    if (isNotFoundError) {
      setPollingStatus(ShortCodePollingStatus.NotFound);
    }
  }, [error]);

  return {
    pollingShortCodeStatus: pollingStatus,
    resetPollingStatus,
  };
};
